import { motion, useReducedMotion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useRef, useState } from 'react';
import { mutate } from 'swr';

import type { AgentAttachment } from '@commenda/types';

import {
  commendaAiConversationsKey,
  commendaAiPendingKey,
  createCommendaAiConversation,
} from '@commenda/api/commenda-ai/api';
import type { IDocumentReaderUploadReference } from '@commenda/api/commenda-ai/document-reader/types';
import { toast } from '@commenda/kakashi';
import { ChatComposer } from '@commenda/ui/commenda-ai/ChatComposer';
import { DataRoomSendHelper } from '@commenda/ui/commenda-ai/data-room/DataRoomSendHelper';
import { useConfig } from 'context/useConfig';

export const HomePromptInput = () => {
  const router = useRouter();

  const { companyId, isLoading: isConfigLoading } = useConfig();

  const prefersReducedMotion = useReducedMotion();

  const [isLeaving, setIsLeaving] = useState(false);

  const creatingRef = useRef(false);
  const pendingConversationRef = useRef<Promise<string> | null>(null);

  const handleSubmit = (
    text: string,
    attachments: AgentAttachment[],
    dataRoom?: IDocumentReaderUploadReference
  ) => {
    if (creatingRef.current || isConfigLoading) return;
    creatingRef.current = true;

    pendingConversationRef.current = (async () => {
      const created = await createCommendaAiConversation(companyId);
      if (!created.success || !created.data) {
        toast.error(created.error ?? 'Could not start the chat.');
        throw new Error(created.error);
      }

      const conversation = created.data;
      window.sessionStorage.setItem(
        commendaAiPendingKey(conversation.id),
        DataRoomSendHelper.toPendingMessage(text, attachments, dataRoom ?? null)
      );

      await mutate(commendaAiConversationsKey(companyId, true));

      return conversation.id;
    })();

    setIsLeaving(true);
  };

  const handleAnimationComplete = async () => {
    if (!isLeaving) return;

    try {
      const conversationId = await pendingConversationRef.current;

      if (conversationId) await router.push(`/agent?c=${conversationId}`);
    } catch {
      creatingRef.current = false;

      setIsLeaving(false);
    }
  };

  return (
    <motion.div
      initial={false}
      animate={
        isLeaving
          ? { y: prefersReducedMotion ? 0 : 12, opacity: 0 }
          : { y: 0, opacity: 1 }
      }
      transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
      onAnimationComplete={handleAnimationComplete}
    >
      <ChatComposer
        placeholder="Describe your task or ask a question"
        onSubmit={handleSubmit}
        dataRoomTarget={{ conversationId: null, companyId }}
        showDisclaimer={false}
      />
    </motion.div>
  );
};
